import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Bot, Search, RefreshCw, Play, Pause, Zap, Clock, BellRing, AlertCircle,
  UserCog, ServerCog, ShieldAlert,
} from 'lucide-react';
import PageShell from '../../components/PageShell';
import Card from '../../components/Card';
import Button from '../../components/Button';
import EmptyState from '../../components/EmptyState';
import LoadingSpinner from '../../components/LoadingSpinner';
import { AgentStatusBadge, HeartbeatControl } from '../../components/agent/AgentBits';
import {
  userAgentsApi, formatInterval,
  type AgentTemplate, type UserAgent,
} from '../../api/userAgents';
import { adminApi, type AdminUserSummary } from '../../api/admin';
import { timeAgo, timeAgoWithDate } from '../../utils/formatTime';

const FALLBACK_BOUNDS = { min: 30, max: 604800 };

/**
 * Admin → User Agents. Owner/admin only. Pick a user, see their agents with the
 * personal focus prompt shown **verbatim**, and act on them (enable/disable,
 * adjust heartbeat within the template bounds, wake now). System-only agents
 * (ops / dispatcher / core) get their own section.
 *
 * `system_prompt` is never requested or rendered — anywhere.
 */
export default function UserAgentsAdmin({ embedded = false }: { embedded?: boolean }) {
  const [users, setUsers] = useState<AdminUserSummary[]>([]);
  const [userSearch, setUserSearch] = useState('');
  const [selectedUser, setSelectedUser] = useState<AdminUserSummary | null>(null);
  const [agents, setAgents] = useState<UserAgent[]>([]);
  const [systemAgents, setSystemAgents] = useState<UserAgent[]>([]);
  const [systemTemplates, setSystemTemplates] = useState<AgentTemplate[]>([]);
  const [templates, setTemplates] = useState<AgentTemplate[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [loadingAgents, setLoadingAgents] = useState(false);
  const [error, setError] = useState('');
  const [toast, setToast] = useState('');
  const [busyId, setBusyId] = useState('');

  const boundsByKey = useMemo(() => {
    const map = new Map<string, AgentTemplate>();
    templates.forEach(t => map.set(t.key, t));
    return map;
  }, [templates]);

  const loadUsers = useCallback(async () => {
    setLoadingUsers(true);
    try {
      const res = await adminApi.listUsers({ page: 1, per_page: 50, search: userSearch });
      setUsers(res.users);
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Could not load users.');
    } finally { setLoadingUsers(false); }
  }, [userSearch]);

  const loadTemplates = useCallback(async () => {
    try { setTemplates(await userAgentsApi.listTemplates()); } catch { /* bounds fall back */ }
  }, []);

  const loadSystemAgents = useCallback(async (userId?: string) => {
    try {
      const section = await userAgentsApi.adminSystemOnly(userId);
      setSystemTemplates(section.templates ?? []);
      setSystemAgents(section.instances ?? []);
    } catch { setSystemTemplates([]); setSystemAgents([]); }
  }, []);

  const loadAgents = useCallback(async (userId: string) => {
    setLoadingAgents(true); setError('');
    try {
      setAgents(await userAgentsApi.adminList(userId));
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Could not load this user’s agents.');
      setAgents([]);
    } finally { setLoadingAgents(false); }
  }, []);

  useEffect(() => { void loadUsers(); }, [loadUsers]);
  useEffect(() => { void loadTemplates(); }, [loadTemplates]);
  useEffect(() => {
    if (selectedUser) { void loadAgents(selectedUser.id); void loadSystemAgents(selectedUser.id); }
    else { setAgents([]); void loadSystemAgents(); }
  }, [selectedUser, loadAgents, loadSystemAgents]);

  const showToast = (msg: string) => {
    setToast(msg);
    window.setTimeout(() => setToast(''), 4000);
  };

  const withBusy = async (id: string, fn: () => Promise<unknown>) => {
    setBusyId(id); setError('');
    try { await fn(); } catch (e: any) { setError(e?.response?.data?.detail || 'Action failed.'); }
    finally { setBusyId(''); }
  };

  const toggleStatus = (agent: UserAgent) => withBusy(agent.id, async () => {
    const next = agent.status === 'enabled' ? 'paused' : 'enabled';
    await userAgentsApi.adminUpdate(agent.id, { status: next, heartbeat_enabled: next === 'enabled' });
    showToast(next === 'enabled' ? `${agent.name} enabled.` : `${agent.name} disabled.`);
    if (selectedUser) await loadAgents(selectedUser.id);
    await loadSystemAgents(selectedUser?.id);
  });

  const wakeNow = (agent: UserAgent) => withBusy(agent.id, async () => {
    await userAgentsApi.wake(agent.id);
    showToast(`${agent.name} woken.`);
    if (selectedUser) await loadAgents(selectedUser.id);
    await loadSystemAgents(selectedUser?.id);
  });

  const saveHeartbeat = (agent: UserAgent, seconds: number) => withBusy(agent.id, async () => {
    await userAgentsApi.adminUpdate(agent.id, { heartbeat_seconds: seconds });
    showToast(`${agent.name} heartbeat set to ${formatInterval(seconds)}.`);
    if (selectedUser) await loadAgents(selectedUser.id);
    await loadSystemAgents(selectedUser?.id);
  });

  const body = (
    <div className="space-y-5">
      {toast && (
        <div className="fixed bottom-5 right-5 z-50 flex items-center gap-2 rounded-lg border border-green-300 bg-green-50 px-4 py-3 text-sm font-medium text-green-700 shadow-lg dark:border-green-800 dark:bg-green-900/40 dark:text-green-300">
          <Bot size={16} />{toast}
        </div>
      )}

      {error && (
        <div className="flex items-center gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2.5 text-sm text-amber-800 dark:border-amber-800 dark:bg-amber-900/30 dark:text-amber-300">
          <AlertCircle size={16} />{error}
        </div>
      )}

      <div className="grid gap-5 lg:grid-cols-[320px,1fr]">
        {/* User picker */}
        <Card className="flex max-h-[75vh] flex-col gap-3 overflow-hidden">
          <div className="flex items-center gap-2">
            <UserCog size={18} className="text-slate-500" />
            <div>
              <h2 className="text-sm font-semibold text-slate-900 dark:text-slate-100">Pick a user</h2>
              <p className="text-xs text-slate-500">See and manage that user’s agents.</p>
            </div>
          </div>
          <div className="relative">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              value={userSearch}
              onChange={e => setUserSearch(e.target.value)}
              placeholder="Search by name or email…"
              className="w-full rounded-lg border border-slate-300 bg-white py-2 pl-9 pr-3 text-sm text-slate-950 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
            />
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto">
            {loadingUsers ? (
              <div className="flex h-24 items-center justify-center"><LoadingSpinner /></div>
            ) : users.length === 0 ? (
              <p className="px-1 py-6 text-center text-xs text-slate-500">No users found.</p>
            ) : (
              <ul className="space-y-1">
                {users.map(u => {
                  const active = selectedUser?.id === u.id;
                  return (
                    <li key={u.id}>
                      <button
                        type="button"
                        onClick={() => setSelectedUser(u)}
                        className={`w-full rounded-lg px-3 py-2 text-left text-sm transition-colors ${
                          active
                            ? 'bg-brand-600/15 text-brand-700 dark:text-brand-300'
                            : 'text-slate-700 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800'
                        }`}
                      >
                        <span className="flex items-center gap-2">
                          <span className="truncate font-medium">{u.name || u.email}</span>
                          {u.is_owner && <span className="rounded bg-amber-500/10 px-1.5 py-0.5 text-[10px] font-medium text-amber-700 dark:text-amber-300">OWNER</span>}
                        </span>
                        <span className="block truncate text-xs text-slate-500">{u.email}</span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </Card>

        {/* Agents for the selected user */}
        <div className="space-y-4">
          <div className="flex items-center justify-between gap-2">
            <h2 className="text-base font-semibold text-slate-950 dark:text-slate-100">
              {selectedUser ? `${selectedUser.name || selectedUser.email}’s agents` : 'Agents'}
            </h2>
          </div>

          {!selectedUser ? (
            <EmptyState icon={<UserCog size={28} />} title="Select a user" description="Pick a user on the left to see their agents and personal focus prompts." />
          ) : loadingAgents ? (
            <div className="flex h-32 items-center justify-center"><LoadingSpinner size="lg" /></div>
          ) : agents.length === 0 ? (
            <EmptyState icon={<Bot size={28} />} title="No agents for this user" description="This user hasn’t added any agents yet." />
          ) : (
            <div className="grid gap-4 md:grid-cols-2">
              {agents.map(agent => (
                <AdminAgentCard
                  key={agent.id}
                  agent={agent}
                  template={boundsByKey.get(agent.template_key)}
                  busy={busyId === agent.id}
                  onToggle={() => toggleStatus(agent)}
                  onWake={() => wakeNow(agent)}
                  onSaveHeartbeat={seconds => saveHeartbeat(agent, seconds)}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* System-only agents */}
      <div className="space-y-3 border-t border-slate-200 pt-5 dark:border-slate-800">
        <div className="flex items-center gap-2">
          <ServerCog size={18} className="text-slate-500" />
          <div>
            <h2 className="text-base font-semibold text-slate-950 dark:text-slate-100">System agents</h2>
            <p className="text-sm text-slate-500">Platform-owned agents (ops, coordinator, core). Hidden from end users.</p>
          </div>
        </div>
        {systemAgents.length === 0 ? (
          systemTemplates.length > 0 ? (
            <p className="text-xs text-slate-500">
              No system-agent instances yet. Available types: {systemTemplates.map(t => t.name).join(', ')}.
            </p>
          ) : (
            <p className="text-xs text-slate-500">No system agents reported.</p>
          )
        ) : (
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {systemAgents.map(agent => (
              <AdminAgentCard
                key={agent.id}
                agent={agent}
                template={boundsByKey.get(agent.template_key)}
                busy={busyId === agent.id}
                system
                onToggle={() => toggleStatus(agent)}
                onWake={() => wakeNow(agent)}
                onSaveHeartbeat={seconds => saveHeartbeat(agent, seconds)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );

  if (embedded) return body;
  return (
    <PageShell
      title="User Agents"
      purpose="Every user’s virtual assistants — with their personal focus prompts shown verbatim."
      actions={<Button variant="secondary" size="sm" onClick={() => { void loadUsers(); void loadSystemAgents(selectedUser?.id); if (selectedUser) void loadAgents(selectedUser.id); }}><RefreshCw size={14} /> Refresh</Button>}
    >
      {body}
    </PageShell>
  );
}

function AdminAgentCard({
  agent, template, busy, system = false, onToggle, onWake, onSaveHeartbeat,
}: {
  agent: UserAgent;
  template?: AgentTemplate;
  busy: boolean;
  system?: boolean;
  onToggle: () => void;
  onWake: () => void;
  onSaveHeartbeat: (seconds: number) => void;
}) {
  const min = template?.min_heartbeat_seconds ?? FALLBACK_BOUNDS.min;
  const max = template?.max_heartbeat_seconds ?? FALLBACK_BOUNDS.max;
  const [draft, setDraft] = useState(agent.heartbeat_seconds);
  useEffect(() => { setDraft(agent.heartbeat_seconds); }, [agent.heartbeat_seconds, agent.id]);

  const paused = agent.status !== 'enabled';
  const dirty = draft !== agent.heartbeat_seconds;

  return (
    <Card className="flex flex-col gap-3">
      <div className="flex items-start justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2">
          <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${system ? 'bg-slate-500/15 text-slate-500' : 'bg-brand-500/15 text-brand-600 dark:text-brand-400'}`}>
            {system ? <ServerCog size={18} /> : <Bot size={18} />}
          </div>
          <div className="min-w-0">
            <p className="truncate font-semibold text-slate-900 dark:text-slate-100">{agent.name}</p>
            <p className="truncate text-xs text-slate-500">{agent.category} · {agent.template_key}</p>
          </div>
        </div>
        <AgentStatusBadge status={agent.status} />
      </div>

      {agent.job && <p className="text-sm text-slate-600 dark:text-slate-400">{agent.job}</p>}

      {/* Personal focus prompt — shown verbatim (owner requirement). */}
      <div className="rounded-lg border border-slate-200 bg-slate-50 p-2.5 dark:border-slate-700 dark:bg-slate-800/50">
        <p className="text-[11px] uppercase tracking-wide text-slate-400">User focus prompt</p>
        {agent.user_focus
          ? <p className="whitespace-pre-wrap text-sm text-slate-700 dark:text-slate-300">{agent.user_focus}</p>
          : <p className="text-sm italic text-slate-400">No focus prompt set.</p>}
      </div>

      {/* Persona — shown verbatim, like the focus prompt. */}
      <div className="rounded-lg border border-slate-200 bg-slate-50 p-2.5 dark:border-slate-700 dark:bg-slate-800/50">
        <p className="text-[11px] uppercase tracking-wide text-slate-400">{agent.category === 'main' ? 'Persona (primary agent)' : 'Persona'}</p>
        {agent.persona
          ? <p className="whitespace-pre-wrap text-sm text-slate-700 dark:text-slate-300">{agent.persona}</p>
          : <p className="text-sm italic text-slate-400">No persona set — speaks plainly.</p>}
      </div>

      <div className="flex flex-wrap gap-4 text-xs text-slate-500">
        <span className="flex items-center gap-1.5"><Clock size={13} />{formatInterval(agent.heartbeat_seconds)}{!agent.heartbeat_enabled && ' (off)'}</span>
        <span className="flex items-center gap-1.5" title={timeAgoWithDate(agent.last_heartbeat_at)}>Last: {timeAgo(agent.last_heartbeat_at)}</span>
        <span className="flex items-center gap-1.5">{agent.queue_depth} queued</span>
        {agent.open_escalations > 0 && (
          <span className="flex items-center gap-1.5 font-medium text-amber-700 dark:text-amber-400"><BellRing size={13} />{agent.open_escalations} open</span>
        )}
      </div>

      <div className="rounded-lg border border-slate-200 p-2.5 dark:border-slate-700">
        <p className="mb-1 text-[11px] uppercase tracking-wide text-slate-400">Heartbeat (within bounds)</p>
        <HeartbeatControl value={draft} min={min} max={max} onChange={setDraft} disabled={busy} />
        {dirty && (
          <div className="mt-2 flex justify-end gap-2">
            <Button size="sm" variant="ghost" onClick={() => setDraft(agent.heartbeat_seconds)} disabled={busy}>Cancel</Button>
            <Button size="sm" onClick={() => onSaveHeartbeat(draft)} disabled={busy}>
              {busy ? <RefreshCw size={14} className="animate-spin" /> : null} Save
            </Button>
          </div>
        )}
      </div>

      <div className="mt-auto flex flex-wrap gap-2">
        <Button size="sm" variant="secondary" onClick={onWake} disabled={busy}><Zap size={14} /> Wake now</Button>
        <Button size="sm" variant="ghost" onClick={onToggle} disabled={busy}>
          {paused ? <><Play size={14} /> Enable</> : <><Pause size={14} /> Disable</>}
        </Button>
      </div>

      {system && agent.open_escalations > 0 && (
        <p className="flex items-center gap-1.5 text-[11px] text-amber-600 dark:text-amber-400">
          <ShieldAlert size={12} />Platform agent has open items.
        </p>
      )}
    </Card>
  );
}
